import React from "react";
import {
  MessageSquare,
  Plug,
  BarChart3,
  CheckCircle,
  ArrowRight,
  BookOpen,
  HelpCircle,
  Mail,
} from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useTheme } from "../../context/ThemeContext";

const HubSpotDocumentation: React.FC = () => {
  const { theme } = useTheme();

  return (
    <div className="flex flex-col w-full min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="subtle-grid"></div>

      <main className="flex-1 flex flex-col px-6 pt-16 pb-16 relative z-10">
        <div className="max-w-4xl mx-auto w-full">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary">
                <BookOpen className="w-5 h-5" />
              </div>
              <h1 className="text-3xl md:text-4xl font-semibold text-black dark:text-text-primaryDark tracking-tight">
                HubSpot Integration Documentation
              </h1>
            </div>
            <p className="text-base text-black/60 dark:text-text-secondaryDark leading-relaxed max-w-2xl">
              Connect your HubSpot account to Forgeworks and unlock AI-powered
              analytics and real-time insights for intelligent data analysis of
              your revenue operations.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="mb-12 p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
            <h2 className="text-sm font-semibold text-black dark:text-text-primaryDark mb-4 uppercase tracking-wider">
              Table of Contents
            </h2>
            <nav className="space-y-2">
              <a
                href="#overview"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Overview
              </a>
              <a
                href="#getting-started"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Getting Started
              </a>
              <a
                href="#setup"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Setup Instructions
              </a>
              <a
                href="#features"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Features
              </a>
              <a
                href="#troubleshooting"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Troubleshooting
              </a>
              <a
                href="#support"
                className="block text-sm text-secondary hover:text-secondary-hover transition-colors"
              >
                Support
              </a>
            </nav>
          </div>

          {/* Overview Section */}
          <section id="overview" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-4">
              Overview
            </h2>
            <div className="space-y-4 text-base text-black/70 dark:text-text-secondaryDark leading-relaxed">
              <p>
                The HubSpot Analytics Agent is a powerful integration that
                connects your HubSpot CRM to Forgeworks, enabling you to analyze
                your sales data, track pipeline metrics and interact with your
                HubSpot instance through an AI-powered chat interface.
              </p>
              <p>
                This integration provides real-time access to your deals,
                contacts, companies and pipeline data, allowing you to ask
                questions, generate insights and make data-driven decisions
                faster.
              </p>
            </div>
          </section>

          {/* Getting Started Section */}
          <section id="getting-started" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-4">
              Getting Started
            </h2>
            <div className="space-y-6">
              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-3">
                  Prerequisites
                </h3>
                <ul className="space-y-2 text-base text-black/70 dark:text-text-secondaryDark">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span>
                      An active HubSpot account with admin or appropriate
                      permissions
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span>
                      A Forgeworks account (sign up at{" "}
                      <a
                        href="/auth"
                        className="text-secondary hover:underline"
                      >
                        app.forgeworks.ai
                      </a>
                      )
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span>Access to install apps in your HubSpot account</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Setup Instructions Section */}
          <section id="setup" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-4">
              Setup Instructions
            </h2>
            <div className="space-y-6">
              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 dark:bg-secondary/10 text-secondary font-semibold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Navigate to the HubSpot Agent
                    </h3>
                    <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      From your Forgeworks dashboard, click on the "HubSpot
                      Analytics Agent" card or navigate to the Agents page and
                      select the HubSpot integration.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 dark:bg-secondary/10 text-secondary font-semibold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Connect Your HubSpot Account
                    </h3>
                    <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed mb-3">
                      Click the "Connect HubSpot" button. You'll be redirected
                      to HubSpot's authorization page where you'll need to:
                    </p>
                    <ul className="space-y-2 text-base text-black/70 dark:text-text-secondaryDark ml-4">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                        <span>
                          Log in to your HubSpot account (if not already logged
                          in)
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                        <span>
                          Review the permissions requested by Forgeworks
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                        <span>Click "Allow" to authorize the connection</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 dark:bg-secondary/10 text-secondary font-semibold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Start Using the Integration
                    </h3>
                    <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      Once connected, you'll be redirected back to Forgeworks.
                      You can now access your HubSpot data, view metrics, and
                      start chatting with the AI agent about your HubSpot
                      instance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-6">
              Features
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      AI-Powered Chat Interface
                    </h3>
                    <p className="text-sm text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      Ask natural language questions about your HubSpot data and
                      get instant, intelligent responses. The AI understands
                      your pipeline, deals, contacts and can help you analyze
                      trends and patterns.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Real-Time Metrics Dashboard
                    </h3>
                    <p className="text-sm text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      View key metrics at a glance including total deals,
                      pipeline value, active contacts and company data. All
                      metrics update in real-time as your HubSpot data changes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                    <Plug className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Secure OAuth Connection
                    </h3>
                    <p className="text-sm text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      Your connection is secured using OAuth 2.0. We never store
                      your HubSpot password and you can revoke access at any
                      time from your HubSpot settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                      Pipeline Analytics
                    </h3>
                    <p className="text-sm text-black/70 dark:text-text-secondaryDark leading-relaxed">
                      Analyze your sales pipeline, identify bottlenecks, track
                      deal progress and understand which stages need attention
                      to optimize your revenue operations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Troubleshooting Section */}
          <section id="troubleshooting" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-6">
              Troubleshooting
            </h2>
            <div className="space-y-4">
              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-3">
                  Connection Issues
                </h3>
                <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed mb-3">
                  If you're having trouble connecting your HubSpot account:
                </p>
                <ul className="space-y-2 text-base text-black/70 dark:text-text-secondaryDark ml-4">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Ensure you have admin permissions in your HubSpot account
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Check that pop-up blockers aren't preventing the
                      authorization window
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Try disconnecting and reconnecting the integration
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Clear your browser cache and cookies, then try again
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
                <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-3">
                  Data Not Updating
                </h3>
                <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed mb-3">
                  If your HubSpot data isn't appearing or updating:
                </p>
                <ul className="space-y-2 text-base text-black/70 dark:text-text-secondaryDark ml-4">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Wait a few moments for data to sync (first sync may take a
                      minute)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>Refresh the page to trigger a new data fetch</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                    <span>
                      Verify your HubSpot connection is still active in your
                      account settings
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Support Section */}
          <section id="support" className="mb-16">
            <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-6">
              Support
            </h2>
            <div className="p-6 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-secondary/10 dark:bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-2">
                    Need Help?
                  </h3>
                  <p className="text-base text-black/70 dark:text-text-secondaryDark leading-relaxed mb-4">
                    Our support team is here to help you get the most out of the
                    HubSpot integration. If you encounter any issues or have
                    questions, don't hesitate to reach out.
                  </p>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-secondary" />
                    <a
                      href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
                      className="text-secondary hover:text-secondary-hover font-medium transition-colors"
                    >
                      {AppConfig.SUPPORT_EMAIL}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 mt-8 border-t border-divider dark:border-divider-dark">
            <p className="text-sm text-black/50 dark:text-text-secondaryDark text-center">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HubSpotDocumentation;
